import { compact } from "lodash";
import type { FC } from "react";
import { useAppState } from "../../context/app/context";
import useHeadTranslation, {
	type LocalesData,
} from "../../hooks/use-head-translation";

type SEO = {
	localesData: LocalesData;
	description: string;
	meta?: { name: string; content: string }[];
	title: string;
	isStartPage?: boolean;
	noIndex?: boolean;
	noFollow?: boolean;
	canonicalUrl?: string;
};

export const SEO: FC<SEO> = ({
	localesData,
	description,
	meta,
	title,
	isStartPage = false,
	noIndex = false,
	noFollow = false,
	canonicalUrl,
}) => {
	const { siteConfig } = useAppState();
	const { t } = useHeadTranslation(localesData);
	const companyName = t("BabyBjörn");
	const companyEnding = ` | ${companyName}`;
	const titleAlreadyHasCompanyEnding = title
		.toLocaleLowerCase()
		.endsWith(companyEnding.toLocaleLowerCase());

	const titleTemplate = isStartPage
		? title
		: `${title}${titleAlreadyHasCompanyEnding ? "" : companyEnding}`; // Don't add "| BabyBjörn" if its already in the meta title

	const defaultMetaTags = compact([
		noIndex ? { name: "robots", content: "noindex" } : undefined,
		noFollow ? { name: "robots", content: "nofollow" } : undefined,
		description
			? {
					name: `description`,
					content: description || undefined,
				}
			: undefined,
		{
			property: `og:title`,
			content: title || undefined,
		},
		description
			? {
					property: `og:description`,
					content: description || undefined,
				}
			: undefined,
		{
			property: `og:type`,
			content: `website`,
		},
		{
			name: `twitter:card`,
			content: `summary`,
		},
		{
			name: `twitter:title`,
			content: title || undefined,
		},
		description
			? {
					name: `twitter:description`,
					content: description || undefined,
				}
			: undefined,
	]);

	const store = process.env.GATSBY_STORE;
	let sameAs = "";
	if (store === "us") {
		sameAs = `"sameAs": ["https://www.instagram.com/babybjornus/",
		"https://www.pinterest.com/babybjorn/",
		"https://www.tiktok.com/@babybjorn",
		"https://www.facebook.com/BabyBjornUS",
		"https://www.youtube.com/@BabyBjorn1961"],`;
	} else if (store === "kr") {
		sameAs = `"sameAs": ["https://www.instagram.com/babybjornkorea/",
		"https://www.youtube.com/@babybjorn7875",
		"https://blog.naver.com/babybjornkorea",
		"https://brand.naver.com/babybjorn"],`;
	}

	return (
		<>
			<title>{title ? titleTemplate : companyName}</title>

			{canonicalUrl && !noIndex && <link rel="canonical" href={canonicalUrl} />}

			{[...defaultMetaTags, ...(meta || [])].map((m, i) => (
				<meta
					key={`${m.name}-${m.content}-${i}`}
					name={m.name}
					content={m.content}
					property={"property" in m ? m.property : ""}
				/>
			))}

			{isStartPage && (
				<script type="application/ld+json">
					{`[{
							"@context": "https://schema.org",
							"@type": "Organization",
							"name": "BabyBjörn",
							"alternateName": ["BabyBjorn"],
							${sameAs}
							"url": "https://${siteConfig.name}/",
							"logo": "/logo/logo.svg"
						},
						{
							"@context": "https://schema.org",
							"@type": "WebSite",
							"name": "BabyBjörn",
							"alternateName": ["BabyBjorn"],
							"url": "https://${siteConfig.name}/"
						}]`}
				</script>
			)}
		</>
	);
};
